import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import AnniversaryIntro from "./components/AnniversaryIntro";
import Home from "./pages/Home";
import Services from "./pages/Services";
import Solutions from "./pages/Solutions";
import Industries from "./pages/Industries";
import Accelerators from "./pages/Accelerators";
import CaseStudies from "./pages/CaseStudies";
import Insights from "./pages/Insights";
import InsightDetail from "./pages/InsightDetail";
import KeystaticApp from "./pages/KeystaticApp";
import Careers from "./pages/Careers";
import UsaCareers from "./pages/UsaCareers";
import IndiaCareers from "./pages/IndiaCareers";
import Contact from "./pages/Contact";
import CaseStudyDetail from "./pages/CaseStudyDetail";

// Lazy-loaded so these ship as separate chunks and show a
// spinner instead of any stale/wrong content while loading.
const Landing = lazy(() => import("./pages/Tissa_Odoo/pages/Landing"));
const ThankYou = lazy(() => import("./pages/Tissa_Odoo/pages/ThankYou"));

function PageLoader() {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#fff",
        zIndex: 9999,
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          border: "4px solid #e4e7ec",
          borderTopColor: "#1769e0",
          borderRadius: "50%",
          animation: "app-spin 0.8s linear infinite",
        }}
      />
      <style>{`
        @keyframes app-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function ScrollToTop() {
  const { pathname, search, hash } = useLocation();

  React.useEffect(() => {
    if (pathname.startsWith("/keystatic")) {
      return;
    }
    // Route transitions should never keep stale modal scroll locks.
    document.body.classList.remove("modal-open");
    document.documentElement.classList.remove("modal-open");
    document.body.style.removeProperty("overflow");
    document.documentElement.style.removeProperty("overflow");
  }, [pathname]);

  React.useEffect(() => {
    // Defensive reset in case any modal flow leaves page scrolling locked.
    document.body.style.removeProperty("overflow");
    document.documentElement.style.removeProperty("overflow");

    if (pathname.startsWith("/keystatic")) {
      return;
    }

    if (hash) {
      const id = decodeURIComponent(hash.replace("#", ""));
      const target = document.getElementById(id);

      if (target) {
        target.scrollIntoView({ block: "start" });
        return;
      }
    }

    window.scrollTo({ top: 0, left: 0 });
  }, [pathname, search, hash]);

  return null;
}

export default function App() {
  function AppLayout() {
    const { pathname } = useLocation();

    // Handle the Odoo funnel pages FIRST, before any other routing
    // logic runs — guarantees Home/Header/Footer can never mount
    // even transiently for these paths.
    // Remove trailing slash for strict route matching if present
    const normalizedPath =
      pathname.endsWith("/") && pathname !== "/"
        ? pathname.slice(0, -1)
        : pathname;

    if (normalizedPath === "/odoo-india") {
      return (
        <Suspense fallback={<PageLoader />}>
          <Landing />
        </Suspense>
      );
    }

    if (pathname === "/thank-you") {
      return (
        <Suspense fallback={<PageLoader />}>
          <ThankYou />
        </Suspense>
      );
    }

    const isKeystaticRoute = pathname.startsWith("/keystatic");

    return (
      <>
        {!isKeystaticRoute && <Header />}
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/industries" element={<Industries />} />
          <Route path="/accelerators" element={<Accelerators />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/:slug" element={<InsightDetail />} />
          <Route path="/keystatic/*" element={<KeystaticApp />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/usa-careers" element={<UsaCareers />} />
          <Route path="/india-careers" element={<IndiaCareers />} />
          <Route path="/contact" element={<Contact />} />
        </Routes>
        {!isKeystaticRoute && <Footer />}
        {!isKeystaticRoute && <AnniversaryIntro />}
      </>
    );
  }

  return (
    <BrowserRouter>
      <ScrollToTop />
      <AppLayout />
    </BrowserRouter>
  );
}
